/** The across-the-room live indicator. The largest thing on a screen operated mid-broadcast. */

import type { CSSProperties, ReactNode } from "react";

import { cn } from "@/lib/cn";

import "./SessionStatus.css";

export type SessionState = "live" | "starting" | "ending" | "off" | "unknown";

const WORD: Record<SessionState, string> = {
  live: "Live",
  starting: "Starting",
  ending: "Ending",
  off: "Off",
  unknown: "Unknown",
};

export interface SessionStatusProps {
  readonly state: SessionState;
  readonly detail?: ReactNode;
  readonly elapsed?: ReactNode;
  readonly elapsedLabel?: ReactNode;
  readonly className?: string;
  readonly style?: CSSProperties;
}

export function SessionStatus({
  state,
  detail,
  elapsed,
  elapsedLabel = "Elapsed",
  className,
  style,
}: SessionStatusProps) {
  return (
    <div
      role="status"
      aria-live="polite"
      className={cn("al-sessionstatus", `al-sessionstatus-${state}`, className)}
      style={style}
    >
      <span
        className={cn("al-sessionstatus-dot", state === "live" ? "al-live-dot" : undefined)}
        aria-hidden="true"
      />
      <span className="al-sessionstatus-word">{WORD[state]}</span>
      {detail !== undefined ? <span className="al-sessionstatus-detail">{detail}</span> : null}
      {elapsed !== undefined ? (
        <span className="al-sessionstatus-elapsed al-tabular">
          <span className="al-sessionstatus-elapsed-label">{elapsedLabel}</span>
          {elapsed}
        </span>
      ) : null}
    </div>
  );
}
